import {mergeWidgetParams} from './params';

// Avoid intermediary className mappings
import {
  widget,
  widget__window,
  widget__window__no_button,
  widget__window_open,
  widget__window_close,
  widget__window__right,
  widget__window__left,
  widget__window__center,
  widget__window__header,
  widget__window__header__active,
  widget__window__header__content,
  widget__window__body,
  widget__notification,
  widget__notification__right,
  widget__notification__left,
  widget__notification__center,
  widget__notification__content,
  widget__notification__line,
  widget__notification__close_container,
  widget__notification__close_container_open,
  widget__notification__close_container_close,
  widget__notification_open,
  widget__notification_close,
  widget__badge,
  widget__badge__right,
  widget__badge__left,
  widget__badge__center,
  widget__badge_active,
  widget__trigger,
  widget__trigger__open,
  widget__trigger__right,
  widget__trigger__left,
  widget__trigger__center,
  widget__inner_close,
} from './floating.scss';

function isFunction(f) {
  if (!f) {
    return false;
  }

  return (
    typeof f === 'function' ||
    // Checking for function properties is a fallback alternative
    (f.constructor && f.call && f.apply)
  );
}

function getScriptURL(scriptName) {
  const script =
    document.currentScript ||
    document.getElementById('verse-widget-script') ||
    document.querySelector('script[src*="' + scriptName + '.js"]');

  return script.src;
}

const widgetPromise = new Promise((resolve) => {
  window.document.addEventListener(
    'widget-loaded',
    (e) => resolve(e.detail),
    false
  );
});

function getWidgetFunction(functionName) {
  return function widgetFunction(...args) {
    widgetPromise
      .then(({widget}) => {
        const fn = widget[functionName];
        if (!isFunction(fn)) {
          throw new Error('Invalid widget function');
        }

        fn(...args);
      })
      .catch((err) => {
        throw new Error(err);
      });
  };
}

function emit(...args) {
  widgetPromise
    .then(({emit}) => {
      emit(...args);
    })
    .catch((err) => {
      throw new Error(err);
    });
}

const addUserData = getWidgetFunction('addUserData');
const on = getWidgetFunction('on');
const sendInit = getWidgetFunction('sendInit');
const setOpen = getWidgetFunction('setOpen');

function isTotallyScrolled(target) {
  return target
    ? target.scrollHeight - target.scrollTop <= target.clientHeight
    : false;
}

function hasTouchScreen() {
  if ('maxTouchPoints' in navigator) {
    return navigator.maxTouchPoints > 0;
  }

  if ('msMaxTouchPoints' in navigator) {
    return navigator.msMaxTouchPoints > 0;
  }

  const mQ = window.matchMedia && matchMedia('(pointer:coarse)');
  if (mQ && mQ.media === '(pointer:coarse)') {
    return !!mQ.matches;
  }

  if ('orientation' in window) {
    return true; // deprecated, but good fallback
  }

  // Only as a last resort, fall back to user agent sniffing
  const UA = navigator.userAgent;
  return (
    /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
    /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA)
  );
}

let open = false;
let opened = false;

let inactivityTimer;

function install(root, data, store) {
  const font = store?.style?.font?.family ?? null;
  const fontSrc = font && store.style.font.src;
  if (font && fontSrc) {
    const fontLink = document.createElement('link');
    fontLink.href = fontSrc;
    fontLink.rel = 'stylesheet';
    document.head.appendChild(fontLink);
  }

  const ifrm = document.createElement('iframe');
  ifrm.title = 'Chat window';
  const app = document.createElement('div');
  app.id = 'indigo-ai-widget-root';
  ifrm.style.width = '100%';
  ifrm.style.height = '100%';
  ifrm.style.maxHeight = '100vh';
  ifrm.style.overflow = 'hidden';
  ifrm.style.border = '0';
  ifrm.style.margin = '0';
  ifrm.style.padding = '0';
  ifrm.setAttribute('scrollable', 'no');
  ifrm.addEventListener('load', () => {
    const win =
      ifrm.contentWindow ||
      ifrm.contentDocument.document ||
      ifrm.contentDocument;
    const _document = win.document;
    const charset = _document.createElement('meta');
    const base = _document.createElement('base');
    const styles = _document.createElement('link');
    const script = _document.createElement('script');
    const areModulesSupported = 'noModule' in script;
    let widgetRoot = '/assets';
    if (ENV === 'production' || ENV === 'test') {
      widgetRoot = data.baseUrl;
    }
    styles.href = widgetRoot + '/widget.css';
    styles.type = 'text/css';
    styles.rel = 'stylesheet';
    charset.setAttribute('charset', 'utf-8');
    base.setAttribute('target', '_blank');
    script.src = `${widgetRoot}/widget${
      ENV === 'production' ? '_standalone' : ''
    }${areModulesSupported ? '' : '_legacy'}.js?token=${data.token}&init=${
      data.init
    }&autostart=${data.autostart ? 'on' : 'off'}${
      data.callback ? `&callback=${data.callback}` : ''
    }`;
    if (areModulesSupported) {
      script.type = 'module';
    }
    script.id = 'verse-widget-script';
    _document.head.appendChild(charset);
    _document.head.appendChild(base);
    _document.head.appendChild(styles);
    if (font && fontSrc) {
      const fontLink = _document.createElement('link');
      fontLink.href = fontSrc;
      fontLink.rel = 'stylesheet';
      _document.head.appendChild(fontLink);
    }
    _document.body.style.margin = 0;
    _document.body.style.padding = 0;
    _document.body.appendChild(app);
    _document.body.appendChild(script);
  });

  const notificationCooldown = parseInt(store.notificationCooldown, 10);
  if (store.floating || store.floating_icon) {
    renderFloatingButton(
      root,
      ifrm,
      store.floating_icon || store.icon,
      store.style.widget,
      data.scrollPassthrough,
      !data.autostart && sendInit,
      Boolean(store.no_floating_button),
      notificationCooldown
    );
  } else {
    root.appendChild(ifrm);
  }

  // Notification
  const showNotificationAfter = parseInt(store.showNotificationAfter, 10);
  const showBadge = Boolean(store.showBadge);
  if (!isNaN(showNotificationAfter)) {
    let notificationCheck = true;
    if (notificationCooldown > 0) {
      try {
        const lastOpened = parseInt(
          localStorage.getItem('indigo-widget-notification-closed'),
          10
        );
        if (Date.now() - lastOpened <= notificationCooldown * 1000) {
          notificationCheck = false;
        }
      } catch (e) {}
    }

    if (notificationCheck) {
      if (showNotificationAfter === 0) {
        showNotification({
          text: store.notificationText,
          locale: store?.locale,
          font,
          style: store?.style?.notification ?? {},
          showBadge,
        });
      } else {
        inactivityTimer = setTimeout(() => {
          showNotification({
            text: store.notificationText,
            locale: store?.locale,
            font,
            style: store?.style?.notification ?? {},
            showBadge,
          });
        }, showNotificationAfter);
      }
    }
  }

  const widgetWindow = document.getElementById('indigo-ai-widget-window');
  let scrollTarget;
  if (!data.scrollPassthrough && widgetWindow) {
    const win =
      ifrm.contentWindow ||
      ifrm.contentDocument.document ||
      ifrm.contentDocument;
    const _document = win.document;

    const onWheel = (e) => {
      if (!scrollTarget) {
        scrollTarget = _document.getElementById(
          'indigo-ai-widget-inner-content'
        );
      }

      if (e.deltaY < 0 && scrollTarget.scrollTop <= 0) {
        e.preventDefault();
        return;
      }

      if (e.deltaY > 0 && isTotallyScrolled(scrollTarget)) {
        e.preventDefault();
        return;
      }

      e.stopPropagation();
    };

    // Internal scroll
    app.addEventListener('wheel', onWheel);
    // Scroll in the "padding" of the iframe
    widgetWindow.addEventListener('wheel', onWheel);
  }
}

function updateWindowClass(el, position = 'right') {
  const positionClass =
    position === 'right'
      ? widget__window__right
      : position === 'left'
      ? widget__window__left
      : position === 'center'
      ? widget__window__center
      : widget__window__right;
  if (el) {
    el.classList.remove(
      widget__window_open,
      widget__window_close,
      widget__window__left,
      widget__window__center,
      widget__window__right
    );
    el.classList.add(
      open ? widget__window_open : widget__window_close,
      positionClass
    );
    if (!open) {
      const animationEndCallback = () => {
        el.removeEventListener('animationend', animationEndCallback);
        el.classList.remove(widget__window_close);
      };
      el.addEventListener('animationend', animationEndCallback);
    }
  }
}

const hasSmallViewport = window.screen.width < 500;
function getIosVersion() {
  if (/iP(hone|od|ad)/.test(navigator.platform)) {
    const v = navigator.appVersion.match(/OS (\d+)_(\d+)_?(\d+)?/);

    return [parseInt(v[1], 10), parseInt(v[2], 10), parseInt(v[3] || 0, 10)];
  }
}
let iOSVersion;
try {
  iOSVersion = getIosVersion()[0];
} catch (e) {
  iOSVersion = null;
}

function handleWidgetOpenStatus(
  callback,
  scrollPassthrough,
  iconColor,
  isOpen = 'auto',
  position = 'right',
  notificationCooldown
) {
  if (opened === false && isFunction(callback)) {
    callback();
  }

  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }

  const targetOpen = isOpen === 'auto' ? !open : isOpen;
  if (targetOpen === open) {
    return;
  }
  open = targetOpen;
  setOpen(open);
  emit(open ? 'open' : 'close');
  if (!opened) {
    opened = true;
    const badge = document.getElementById('indigo-ai-badge');
    badge && (badge.style.display = 'none');
  }
  if (!scrollPassthrough && hasSmallViewport) {
    if (!iOSVersion || iOSVersion >= 13) {
      document.body.style.overflow = open ? 'hidden' : 'auto';
    }
  }
  updateWindowClass(
    document.getElementById('indigo-ai-widget-window'),
    position
  );
  updateFloatingIcon(open, iconColor);
  closeNotification(notificationCooldown > 0);
}

function handleTriggerClick(
  callback,
  scrollPassthrough,
  iconColor,
  isOpen = 'auto',
  position = 'right',
  notificationCooldown
) {
  return function triggerClick() {
    handleWidgetOpenStatus(
      callback,
      scrollPassthrough,
      iconColor,
      isOpen,
      position,
      notificationCooldown
    );
  };
}

function renderFloatingButton(
  root,
  ifrm,
  icon,
  style,
  scrollPassthrough,
  callback,
  hideButton,
  notificationCooldown
) {
  const zIndex = style?.depth;
  const backgroundColor = style?.background;
  const iconColor = style?.color;
  const padding = style?.padding || '8px';
  const position = style?.position || 'right';
  const positionClass =
    position === 'right'
      ? widget__trigger__right
      : position === 'left'
      ? widget__trigger__left
      : position === 'center'
      ? widget__trigger__center
      : widget__trigger__right;
  const notificationClass =
    position === 'right'
      ? widget__notification__right
      : position === 'left'
      ? widget__notification__left
      : position === 'center'
      ? widget__notification__center
      : widget__notification__right;
  const badgeClass =
    position === 'right'
      ? widget__badge__right
      : position === 'left'
      ? widget__badge__left
      : position === 'center'
      ? widget__badge__center
      : widget__badge__right;
  root.innerHTML = `
  <div class="${widget}" style="z-index: ${zIndex}">
    <div class="${widget__window} ${
    hideButton ? widget__window__no_button : ''
  }" id="indigo-ai-widget-window">
      <div class="${widget__window__header} ${
    hideButton ? widget__window__header__active : ''
  }">
      ${
        hideButton
          ? `
        <div id="indigo-ai-widget-header-content" class="${widget__window__header__content}"></div>
        <button
          id="indigo-ai-widget-inner-close"
          class="${widget__inner_close}"
          aria-label="Close the widget"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M0.146447 0.146447C0.341709 -0.0488155 0.658291 -0.0488155 0.853553 0.146447L6 5.29289L11.1464 0.146447C11.3417 -0.0488155 11.6583 -0.0488155 11.8536 0.146447C12.0488 0.341709 12.0488 0.658291 11.8536 0.853553L6.70711 6L11.8536 11.1464C12.0488 11.3417 12.0488 11.6583 11.8536 11.8536C11.6583 12.0488 11.3417 12.0488 11.1464 11.8536L6 6.70711L0.853553 11.8536C0.658291 12.0488 0.341709 12.0488 0.146447 11.8536C-0.0488155 11.6583 -0.0488155 11.3417 0.146447 11.1464L5.29289 6L0.146447 0.853553C-0.0488155 0.658291 -0.0488155 0.341709 0.146447 0.146447Z" fill="#59617E"/>
          </svg>
        </button>
      `
          : ''
      }
      </div>
      <div class="${widget__window__body}" id="indigo-ai-widget"></div>
      <div class="widget__window__footer"></div>
    </div>
    
    ${
      !hideButton
        ? `
    <div
      id="indigo-ai-notification"
      class="${widget__notification} ${notificationClass}"
    >
      <div
        id="indigo-ai-notification-close_container"
        class="${widget__notification__close_container}"
      >
        <button
          id="indigo-ai-notification-close"
          class="${widget__notification_close}"
          aria-label="Close chat notification"
        >
          ${renderCloseIcon('1.2em')}
        </button>
      </div>
      <div
        id="indigo-ai-notification-content"
        class="${widget__notification__content}"
      ></div>
    </div>
    <div
      id="indigo-ai-badge"
      class="${widget__badge} ${badgeClass}"
    >1</div>
    <div
      id="indigo-ai-trigger"
      class="${widget__trigger} ${positionClass}"
      style="background-image: url('${icon}'); background-color: ${backgroundColor}; padding: ${padding}"
    ></div>
    `
        : ''
    }
  </div>
  `;

  document.getElementById('indigo-ai-widget').appendChild(ifrm);

  const onClick = handleTriggerClick(
    callback,
    scrollPassthrough,
    iconColor,
    'auto',
    position,
    notificationCooldown
  );

  const onNotificationClose = () => {
    closeNotification(notificationCooldown > 0);
  };

  if (!hideButton) {
    document
      .getElementById('indigo-ai-trigger')
      .addEventListener('click', onClick);
    document
      .getElementById('indigo-ai-notification-content')
      .addEventListener('click', onClick);
    document
      .getElementById('indigo-ai-notification-close')
      .addEventListener('click', onNotificationClose);
  } else {
    document
      .getElementById('indigo-ai-widget-inner-close')
      .addEventListener('click', onClick);
  }
}

function renderCloseIcon(size, iconColor = '#fff') {
  return `
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="font-size: 16px; width: ${size}; height: ${size}">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M16.707 7.293a1 1 0 0 0-1.414 0L12 10.586 8.707 7.293a1 1 0 1 0-1.414 1.414L10.586 12l-3.293 3.293a1 1 0 1 0 1.414 1.414L12 13.414l3.293 3.293a1 1 0 0 0 1.414-1.414L13.414 12l3.293-3.293a1 1 0 0 0 0-1.414z"
        fill="${iconColor}"
      />
    </svg>
    `;
}

function updateFloatingIcon(open, iconColor) {
  const el = document.getElementById('indigo-ai-trigger');
  if (open && el) {
    el.classList.add(widget__trigger__open);
    el.innerHTML = renderCloseIcon('2.098em', iconColor);
  } else if (el) {
    el.classList.remove(widget__trigger__open);
    el.innerHTML = '';
  }
}

function showNotification({
  text: originalText,
  locale,
  font,
  style,
  showBadge,
}) {
  let defaultText = 'Ciao 👋<br/>come posso aiutarti?';
  let defaultWidth = '160px';
  const lang = locale?.lang;
  style = style || {};
  style.boxSizing = 'content-box';
  if (lang && lang.toLowerCase() === 'en') {
    defaultText = 'Hey there 👋<br/>can I help?';
    defaultWidth = '100px';
  }
  const text = (originalText || defaultText).replace('\n', '<br/>');
  if (!originalText && !style.width && style) {
    style.width = defaultWidth;
  }

  const el = document.getElementById('indigo-ai-notification');
  const contentEl = document.getElementById('indigo-ai-notification-content');
  if (font) {
    contentEl.style.fontFamily = font;
  }
  Object.keys(style).map((key) => {
    contentEl.style[key] = style[key];
  });
  sanitizeText(text).forEach((component) => {
    contentEl.appendChild(component);
  });
  el.classList.add(widget__notification_open);

  if (showBadge) {
    const badgeEl = document.getElementById('indigo-ai-badge');
    badgeEl.classList.add(widget__badge_active);
  }

  const closeEl = document.getElementById(
    'indigo-ai-notification-close_container'
  );
  if (hasTouchScreen()) {
    closeEl.classList.add(widget__notification__close_container_open);
  } else {
    el.addEventListener('mouseenter', () => {
      closeEl.classList.add(widget__notification__close_container_open);
      closeEl.classList.remove(widget__notification__close_container_close);
    });
    el.addEventListener('mouseleave', () => {
      closeEl.classList.add(widget__notification__close_container_close);
      closeEl.classList.remove(widget__notification__close_container_open);
    });
  }
}

function closeNotification(savePreference = false) {
  const el = document.getElementById('indigo-ai-notification');
  if (el) {
    el.classList.add(widget__notification_close);
    el.classList.remove(widget__notification_open);
  }
  if (savePreference) {
    try {
      localStorage.setItem(
        'indigo-widget-notification-closed',
        String(Date.now())
      );
    } catch (e) {}
  }
}

function sanitizeText(text) {
  if (!text || typeof text !== 'string') {
    return '';
  }

  const source = text.toString().replace('<br>', '<br/>');
  const components = source.split('<br/>');

  return components.map((component) => {
    const el = document.createElement('span');
    el.className = widget__notification__line;
    el.textContent = component;

    return el;
  });
}

let $$Conf = {};
if (ENV !== 'production' && ENV !== 'test') {
  // Load test config
  $$Conf = JSON.parse(TEST_CONFIG);
} else if (ENV !== 'test') {
  // Parameter parsing
  const url = new URL(
    getScriptURL(ENV === 'production' ? 'widget' : 'install')
  );
  mergeWidgetParams(url, $$Conf);
}

function initialize() {
  if (ENV === 'test') {
    $$Conf = window.widgetConf;
  }
  let node = document.getElementById($$Conf.root);
  if (!node) {
    node = document.createElement('div');
    node.setAttribute('id', $$Conf.root ?? 'indigo-ai-widget-container');
    document.body.appendChild(node);
  }

  install(
    node,
    {
      ...$$Conf.project,
      init: $$Conf.init,
      autostart: $$Conf.autostart,
      baseUrl: $$Conf.baseUrl,
      callback: $$Conf.callback,
      scrollPassthrough: Boolean($$Conf.scrollPassthrough),
    },
    $$Conf.data
  );

  if (typeof CustomEvent === 'function') {
    const loadedEvent = new CustomEvent('indigo-widget-loaded');
    window.document.dispatchEvent(loadedEvent);
  }
}

function setWidgetOpen(value) {
  const style = $$Conf.data.style.widget;
  const iconColor = style?.color;
  const position = style?.position || 'right';
  const scrollPassthrough = Boolean($$Conf.scrollPassthrough);
  const notificationCooldown = parseInt($$Conf.data.notificationCooldown, 10);

  const isOpen = Boolean(typeof value === 'function' ? value(open) : value);

  handleWidgetOpenStatus(
    !$$Conf.autostart && sendInit,
    scrollPassthrough,
    iconColor,
    isOpen,
    position,
    notificationCooldown
  );
}

if (performance?.timing?.loadEventEnd) {
  initialize();
} else if (ENV !== 'test') {
  window.addEventListener('load', initialize);
}

export default {
  addUserData,
  on,
  initialize,
  setOpen: setWidgetOpen,
};
